/**
 * Pairing Protocol
 *
 * Message types and runtime guards for phone ↔ reader communication
 * via Supabase Realtime Broadcast.
 *
 * All messages are JSON objects with a `type` field.
 * Runtime guards enable safe message handling without type assertions.
 */

// =============================================================================
// Constants
// =============================================================================

/** Max single broadcast message size (bytes) */
export const MAX_BROADCAST_BYTES = 180_000;

/** Target chunk size for chunked transfers (bytes) */
export const CHUNK_BYTE_TARGET = 12_000;

/** Maximum chunks per transfer */
export const MAX_CHUNKS = 400;

/** Timeout waiting for ack (ms) */
export const ACK_TIMEOUT_MS = 5_000;

/** Timeout waiting for hello ack (ms) */
export const HELLO_TIMEOUT_MS = 10_000;

// =============================================================================
// Role Types
// =============================================================================

/** Participant role in the pairing session */
export type Role = 'phone' | 'reader';

/** Transport command types */
export type TransportCommand = 'play' | 'pause' | 'rewind' | 'fastForward' | 'restart';

// =============================================================================
// Handshake Messages
// =============================================================================

/** Phone or reader announces presence */
export interface HelloMessage {
  type: 'hello';
  role: Role;
  ts: number;
}

/** Acknowledgement of hello */
export interface HelloAckMessage {
  type: 'ack';
  for: 'hello';
}

// =============================================================================
// Text Transfer Messages (Small)
// =============================================================================

/** Phone sends text to reader (small, single message) */
export interface SetTextMessage {
  type: 'setText';
  id: string;
  text: string;
  totalBytes: number;
}

/** Reader acknowledges setText receipt */
export interface SetTextAckMessage {
  type: 'ack';
  for: 'setText';
  id: string;
}

// =============================================================================
// Text Transfer Messages (Chunked)
// =============================================================================

/** Begin chunked text transfer */
export interface SetTextBeginMessage {
  type: 'setTextBegin';
  id: string;
  totalChunks: number;
  totalBytes: number;
}

/** Individual chunk of text */
export interface SetTextChunkMessage {
  type: 'setTextChunk';
  id: string;
  i: number;
  chunk: string;
}

/** End chunked text transfer */
export interface SetTextEndMessage {
  type: 'setTextEnd';
  id: string;
}

/** Reader acknowledges complete chunked transfer */
export interface SetTextChunkedAckMessage {
  type: 'ack';
  for: 'setTextChunked';
  id: string;
  receivedChunks: number;
}

// =============================================================================
// Transport Control Messages
// =============================================================================

export interface PlayMessage {
  type: 'play';
}

export interface PauseMessage {
  type: 'pause';
}

export interface RewindMessage {
  type: 'rewind';
  words: number;
}

export interface FastForwardMessage {
  type: 'fastForward';
  words: number;
}

export interface RestartMessage {
  type: 'restart';
}

/** Reader acknowledges transport command */
export interface TransportAckMessage {
  type: 'ack';
  for: 'transport';
  cmd: TransportCommand;
  ts: number;
}

// =============================================================================
// Settings Messages
// =============================================================================

export interface SetWpmMessage {
  type: 'setWpm';
  wpm: number;
}

export interface SetWpmAckMessage {
  type: 'ack';
  for: 'setWpm';
  wpm: number;
}

// =============================================================================
// Union Types
// =============================================================================

/** All ack message types */
export type AckMessage =
  | HelloAckMessage
  | SetTextAckMessage
  | SetTextChunkedAckMessage
  | TransportAckMessage
  | SetWpmAckMessage;

/** All message types sent by phone */
export type PhoneMessage =
  | HelloMessage
  | SetTextMessage
  | SetTextBeginMessage
  | SetTextChunkMessage
  | SetTextEndMessage
  | PlayMessage
  | PauseMessage
  | RewindMessage
  | FastForwardMessage
  | RestartMessage
  | SetWpmMessage;

/** All message types sent by reader */
export type ReaderMessage = HelloMessage | AckMessage;

/** Any protocol message */
export type ProtocolMessage = PhoneMessage | ReaderMessage;

// =============================================================================
// Runtime Guards
// =============================================================================

/**
 * Check if value is an object (not null, not array)
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Guard for HelloMessage
 */
export function isHelloMessage(msg: unknown): msg is HelloMessage {
  return (
    isObject(msg) &&
    msg.type === 'hello' &&
    (msg.role === 'phone' || msg.role === 'reader') &&
    typeof msg.ts === 'number'
  );
}

/**
 * Guard for HelloAckMessage
 */
export function isHelloAckMessage(msg: unknown): msg is HelloAckMessage {
  return isObject(msg) && msg.type === 'ack' && msg.for === 'hello';
}

/**
 * Guard for SetTextMessage
 */
export function isSetTextMessage(msg: unknown): msg is SetTextMessage {
  return (
    isObject(msg) &&
    msg.type === 'setText' &&
    typeof msg.id === 'string' &&
    typeof msg.text === 'string' &&
    typeof msg.totalBytes === 'number'
  );
}

/**
 * Guard for SetTextAckMessage
 */
export function isSetTextAckMessage(msg: unknown): msg is SetTextAckMessage {
  return (
    isObject(msg) &&
    msg.type === 'ack' &&
    msg.for === 'setText' &&
    typeof msg.id === 'string'
  );
}

/**
 * Guard for SetTextBeginMessage
 */
export function isSetTextBeginMessage(msg: unknown): msg is SetTextBeginMessage {
  return (
    isObject(msg) &&
    msg.type === 'setTextBegin' &&
    typeof msg.id === 'string' &&
    typeof msg.totalChunks === 'number' &&
    typeof msg.totalBytes === 'number'
  );
}

/**
 * Guard for SetTextChunkMessage
 */
export function isSetTextChunkMessage(msg: unknown): msg is SetTextChunkMessage {
  return (
    isObject(msg) &&
    msg.type === 'setTextChunk' &&
    typeof msg.id === 'string' &&
    typeof msg.i === 'number' &&
    typeof msg.chunk === 'string'
  );
}

/**
 * Guard for SetTextEndMessage
 */
export function isSetTextEndMessage(msg: unknown): msg is SetTextEndMessage {
  return isObject(msg) && msg.type === 'setTextEnd' && typeof msg.id === 'string';
}

/**
 * Guard for SetTextChunkedAckMessage
 */
export function isSetTextChunkedAckMessage(
  msg: unknown
): msg is SetTextChunkedAckMessage {
  return (
    isObject(msg) &&
    msg.type === 'ack' &&
    msg.for === 'setTextChunked' &&
    typeof msg.id === 'string' &&
    typeof msg.receivedChunks === 'number'
  );
}

/**
 * Guard for PlayMessage
 */
export function isPlayMessage(msg: unknown): msg is PlayMessage {
  return isObject(msg) && msg.type === 'play';
}

/**
 * Guard for PauseMessage
 */
export function isPauseMessage(msg: unknown): msg is PauseMessage {
  return isObject(msg) && msg.type === 'pause';
}

/**
 * Guard for RewindMessage
 */
export function isRewindMessage(msg: unknown): msg is RewindMessage {
  return isObject(msg) && msg.type === 'rewind' && typeof msg.words === 'number';
}

/**
 * Guard for FastForwardMessage
 */
export function isFastForwardMessage(msg: unknown): msg is FastForwardMessage {
  return isObject(msg) && msg.type === 'fastForward' && typeof msg.words === 'number';
}

/**
 * Guard for RestartMessage
 */
export function isRestartMessage(msg: unknown): msg is RestartMessage {
  return isObject(msg) && msg.type === 'restart';
}

/**
 * Guard for TransportAckMessage
 */
export function isTransportAckMessage(msg: unknown): msg is TransportAckMessage {
  const validCmds: TransportCommand[] = [
    'play',
    'pause',
    'rewind',
    'fastForward',
    'restart',
  ];
  return (
    isObject(msg) &&
    msg.type === 'ack' &&
    msg.for === 'transport' &&
    validCmds.includes(msg.cmd as TransportCommand) &&
    typeof msg.ts === 'number'
  );
}

/**
 * Guard for SetWpmMessage
 */
export function isSetWpmMessage(msg: unknown): msg is SetWpmMessage {
  return isObject(msg) && msg.type === 'setWpm' && typeof msg.wpm === 'number';
}

/**
 * Guard for SetWpmAckMessage
 */
export function isSetWpmAckMessage(msg: unknown): msg is SetWpmAckMessage {
  return (
    isObject(msg) &&
    msg.type === 'ack' &&
    msg.for === 'setWpm' &&
    typeof msg.wpm === 'number'
  );
}

/**
 * Guard for any ack message
 */
export function isAckMessage(msg: unknown): msg is AckMessage {
  return (
    isHelloAckMessage(msg) ||
    isSetTextAckMessage(msg) ||
    isSetTextChunkedAckMessage(msg) ||
    isTransportAckMessage(msg) ||
    isSetWpmAckMessage(msg)
  );
}

/**
 * Guard for any transport command message
 */
export function isTransportMessage(
  msg: unknown
): msg is PlayMessage | PauseMessage | RewindMessage | FastForwardMessage | RestartMessage {
  return (
    isPlayMessage(msg) ||
    isPauseMessage(msg) ||
    isRewindMessage(msg) ||
    isFastForwardMessage(msg) ||
    isRestartMessage(msg)
  );
}

// =============================================================================
// Message Factories
// =============================================================================

/**
 * Create a hello message
 */
export function createHelloMessage(role: Role): HelloMessage {
  return {
    type: 'hello',
    role,
    ts: Date.now(),
  };
}

/**
 * Create a hello ack message
 */
export function createHelloAckMessage(): HelloAckMessage {
  return {
    type: 'ack',
    for: 'hello',
  };
}

/**
 * Create a setText message
 */
export function createSetTextMessage(
  id: string,
  text: string,
  totalBytes: number
): SetTextMessage {
  return {
    type: 'setText',
    id,
    text,
    totalBytes,
  };
}

/**
 * Create a setText ack message
 */
export function createSetTextAckMessage(id: string): SetTextAckMessage {
  return {
    type: 'ack',
    for: 'setText',
    id,
  };
}

/**
 * Create a setTextBegin message
 */
export function createSetTextBeginMessage(
  id: string,
  totalChunks: number,
  totalBytes: number
): SetTextBeginMessage {
  return {
    type: 'setTextBegin',
    id,
    totalChunks,
    totalBytes,
  };
}

/**
 * Create a setTextChunk message
 */
export function createSetTextChunkMessage(
  id: string,
  i: number,
  chunk: string
): SetTextChunkMessage {
  return {
    type: 'setTextChunk',
    id,
    i,
    chunk,
  };
}

/**
 * Create a setTextEnd message
 */
export function createSetTextEndMessage(id: string): SetTextEndMessage {
  return {
    type: 'setTextEnd',
    id,
  };
}

/**
 * Create a setTextChunked ack message
 */
export function createSetTextChunkedAckMessage(
  id: string,
  receivedChunks: number
): SetTextChunkedAckMessage {
  return {
    type: 'ack',
    for: 'setTextChunked',
    id,
    receivedChunks,
  };
}

/**
 * Create a play message
 */
export function createPlayMessage(): PlayMessage {
  return { type: 'play' };
}

/**
 * Create a pause message
 */
export function createPauseMessage(): PauseMessage {
  return { type: 'pause' };
}

/**
 * Create a rewind message
 */
export function createRewindMessage(words: number = 10): RewindMessage {
  return { type: 'rewind', words };
}

/**
 * Create a fastForward message
 */
export function createFastForwardMessage(words: number = 10): FastForwardMessage {
  return { type: 'fastForward', words };
}

/**
 * Create a restart message
 */
export function createRestartMessage(): RestartMessage {
  return { type: 'restart' };
}

/**
 * Create a transport ack message
 */
export function createTransportAckMessage(cmd: TransportCommand): TransportAckMessage {
  return {
    type: 'ack',
    for: 'transport',
    cmd,
    ts: Date.now(),
  };
}

/**
 * Create a setWpm message
 */
export function createSetWpmMessage(wpm: number): SetWpmMessage {
  return { type: 'setWpm', wpm };
}

/**
 * Create a setWpm ack message
 */
export function createSetWpmAckMessage(wpm: number): SetWpmAckMessage {
  return {
    type: 'ack',
    for: 'setWpm',
    wpm,
  };
}

/**
 * Generate a unique transfer ID
 */
export function generateTransferId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
